import { useCallback, useMemo } from "react";
import { SrId } from "@evergis/react";
import { GeometryType, AttributeType, AttributeFormatDc } from "@evergis/api";
import { useTranslation } from "react-i18next";

import {
  AREA_ATTRIBUTE,
  LINE_ATTRIBUTE,
  CENTROID_ATTRIBUTE,
  GEOMETRY_ATTRIBUTE,
  STATUS_ATTRIBUTE,
  NAME_ATTRIBUTE,
  DATE_OF_IMAGE_ATTRIBUTE,
} from "../../evergis/constants";

import { EditGeometryType } from "../../evergis/core/attributes/geometryTypes";
import { LayerInfo } from "../../interfaces/resourceInfo";

export type CalculatingAttributes = Pick<LayerInfo, "name"> & {
  geometryType?: GeometryType | EditGeometryType;
};

export type CalculatedAttributes = {
  name: string;
  type: AttributeType;
  expression: string;
  alias?: string;
  stringFormat?: AttributeFormatDc;
};

export const projectCalculatedAttribute = (attributeName: string, expression: string) =>
  `${expression}(project(${attributeName}, ${SrId.Geo}))`;

const DEFAULT_ATTRIBUTE = {
  name: "Attribute1",
  type: AttributeType.String,
  expression: null,
};

// eslint-disable-next-line max-lines-per-function
export const useAddCalculatedAttributes = (
  attributes: CalculatingAttributes[],
  withoutDefaultAttribute?: boolean,
): CalculatedAttributes[] => {
  const { t } = useTranslation("common");

  const calculateAttributes = useCallback(
    // eslint-disable-next-line max-lines-per-function
    ({ name, geometryType }: CalculatingAttributes) => {
      const geometryAttributeName = name || GEOMETRY_ATTRIBUTE;

      switch (geometryType) {
        case GeometryType.Polygon: {
          return [
            {
              name: AREA_ATTRIBUTE,
              alias: t("area"),
              type: AttributeType.Double,
              expression: `area(${geometryAttributeName})`,
              stringFormat: {
                rounding: 2,
                unitsLabel: t("sqMeter"),
                splitDigitGroup: true,
              },
            },
            !withoutDefaultAttribute && DEFAULT_ATTRIBUTE,
          ].filter(Boolean);
        }
        case GeometryType.Polyline: {
          return [
            {
              name: LINE_ATTRIBUTE,
              alias: t("length"),
              type: AttributeType.Double,
              expression: `length(${geometryAttributeName})`,
              stringFormat: {
                rounding: 2,
                unitsLabel: t("meter"),
                splitDigitGroup: true,
              },
            },
            !withoutDefaultAttribute && DEFAULT_ATTRIBUTE,
          ].filter(Boolean);
        }
        case GeometryType.Point: {
          return [
            {
              name: CENTROID_ATTRIBUTE,
              alias: t("coordinates"),
              type: AttributeType.Point,
              expression: projectCalculatedAttribute(geometryAttributeName, "coordinate"),
              stringFormat: {
                format: "DD.DD",
              },
            },
            !withoutDefaultAttribute && DEFAULT_ATTRIBUTE,
          ].filter(Boolean);
        }
        case EditGeometryType.Raster: {
          return [
            {
              name: NAME_ATTRIBUTE,
              alias: t("text"),
              type: AttributeType.String,
              expression: null,
            },
            {
              name: DATE_OF_IMAGE_ATTRIBUTE,
              alias: t("dateTime"),
              type: AttributeType.DateTime,
              expression: null,
            },
            {
              name: STATUS_ATTRIBUTE,
              alias: t("text"),
              type: AttributeType.String,
              expression: null,
            },
          ];
        }
        case GeometryType.Unknown: {
          return !withoutDefaultAttribute ? [DEFAULT_ATTRIBUTE] : [];
        }
        default:
          return !withoutDefaultAttribute
            ? [
                {
                  name: CENTROID_ATTRIBUTE,
                  alias: t("coordinates"),
                  type: AttributeType.Point,
                  expression: projectCalculatedAttribute(geometryAttributeName, "coordinate"),
                  stringFormat: {
                    format: "DD.DD",
                  },
                },
                DEFAULT_ATTRIBUTE,
              ]
            : [];
      }
    },
    [t, withoutDefaultAttribute],
  );

  return useMemo(
    (): CalculatedAttributes[] =>
      attributes.reduce(
        (result, attribute) =>
          result.some(item => item.name === attribute.name) ? result : [...result, ...calculateAttributes(attribute)],
        [],
      ),
    [attributes, calculateAttributes],
  );
};
